import clsx from 'clsx';
import React, { HTMLAttributes, useState } from 'react';
import { useHistory } from '@useHistory/useHistoryCompat';
import { throwError } from 'rxjs';
import { Activation } from '../models/activation';
import {
  ActivationStateEnum,
  ActivationStatusEnum
} from '../models/activation-status';
import { ActivationStrings } from '../strings';
import { ENTITY_DELETE_DIALOG_COMMON_CONFIG_REACT } from '../../../core/constants/constants';
import { HevoEntity } from '../../../core/interfaces/hevo-entity';
import { RbacPermissions } from '../../../core/interfaces/user';
import { getErrorMessageFromObj } from '../../../core/utils/request';
import { ConfirmDialogV2 } from '../../../modal/components/confirm-dialog-v2/ConfirmDialogV2';
import NameEditor from '../../../input/components/name-editor/NameEditor';
import { PlayButton } from '../../../button/components/play-button/PlayButton';
import {
  HdIcon,
  HdIconButton,
  HdMenu,
  HdRbacMenuItem,
  HdTooltip
} from '../../../components';
import { HdRbacLink } from '../../../access-control/components/rbac-link/HdRbacLink';
import { WorkflowDependentExecutionPolicy } from '../../scheduler/WorkflowDependentExecutionPolicy';
import useToaster from '../../../toaster/hooks/useToaster';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { ActivationNodeRelation } from '../ActivationNodeRelation/ActivationNodeRelation';
import { ExecutionPolicyDialog } from '../../scheduler/ExecutionPolicyDialog/ExecutionPolicyDialog';
import ActivationsAPI from '../api/ActivationsAPI';
import styles from './styles.module.scss';

const MORE_ACTIONS_DROPDOWN_TRIGGER = 'activation_header_more_trigger';

export function ActivationHeader({
  activation,
  onActivationEdit,
  ...htmlAttributes
}: {
  activation: Activation;
  onActivationEdit: Function;
} & HTMLAttributes<HTMLElement>) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [nameEditorValue, setNameEditorValue] = useState(activation.name);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const [showExecutionPolicyDialog, setShowExecutionPolicyDialog] = useState(false);

  const history = useHistory();
  const toaster = useToaster();

  const dataIdGenerator = getDataIdGenerator('activation-header');

  const getNameUpdatePromise = (name: string) => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      toaster.pop('error', undefined, 'Activation name cannot be empty.');

      return Promise.reject();
    }

    return ActivationsAPI.updateActivation(activation.id, { name: trimmedName })
      .then((res: any) => {
        return res;
      })
      .catch((err: any) => {
        toaster.pop('error', undefined, getErrorMessageFromObj(err));

        return throwError(err);
      });
  };

  const onNameUpdate = () => {
    onActivationEdit();
  };

  const deleteActivationClick = () => {
    setDeleteConfirmationVisible(true);
    setMenuOpen(false);
  };

  const deleteActivation = () => {
    ActivationsAPI.deleteActivation(activation.id).then(
      () => {
        history.replace('/activation');
      },
      () => {}
    );
  };

  const pauseActivation = () => {
    ActivationsAPI.pauseActivation(activation.id).then(
      () => {
        if (activation.status !== ActivationStatusEnum.FAILED) {
          activation.status = ActivationStatusEnum.PAUSED;
        }

        activation.state = ActivationStateEnum.PAUSED;

        onActivationEdit();
      },
      () => {}
    );
  };

  const resumeActivation = () => {
    ActivationsAPI.resumeActivation(activation.id).then(
      () => {
        if (activation.status !== ActivationStatusEnum.FAILED) {
          activation.status = ActivationStatusEnum.ACTIVE;
        }

        activation.state = ActivationStateEnum.ACTIVE;

        onActivationEdit();
      },
      () => {}
    );
  };

  const runNow = () => {
    ActivationsAPI.runNow(activation.id).then(
      () => {
        onActivationEdit();
      },
      () => {}
    );
  };

  const restartActivation = () => {
    ActivationsAPI.restartActivation(activation.id).then(
      () => {
        onActivationEdit();
      },
      () => {}
    );
  };

  const changeFrequencyClick = () => {
    setShowExecutionPolicyDialog(true);
  };

  const onExecutionPolicyDialogClose = (action: boolean) => {
    setShowExecutionPolicyDialog(false);

    if (action) {
      onActivationEdit();
    }
  };

  const policyMessage = `Every ${activation.executionPolicy.frequencyValue} ${activation.executionPolicy.frequencyType.name}`;

  return (
    <header className='secondary-nav floating-header' {...htmlAttributes}>
      <div className={styles.activateHeader}>
        <div className={clsx('entity-id-container', styles.entityIdContainer)}>
          <span className={clsx('entity-id', styles.entityId)}>#{activation.seqId}</span>

          <span className={clsx('entity-type mt-1', styles.entityType)}>
            <HdIcon name='activate' className='mr-1' size={1} />
            Activation
          </span>
        </div>

        <div className={styles.activateHeaderInfo}>
          <div className={styles.activateHeaderNameEditor}>
            <NameEditor
              rbacPermission={RbacPermissions.ACTIVATION_EDIT}
              dataId={dataIdGenerator('activation-name')}
              name={nameEditorValue}
              setName={setNameEditorValue}
              onCancel={() => setNameEditorValue(activation.name)}
              getNameUpdatePromise={getNameUpdatePromise}
              saveSuccess={onNameUpdate}
            />
          </div>

          <ActivationNodeRelation className='mt-1' activation={activation} />
        </div>

        <div className={styles.activateHeaderStatusFrequencyWrapper}>
          <div className='center-flex-row'>
            <div className={`badge badge-solid-faded ${activation.status.colorType}-badge`}>
              {activation.status.name}
            </div>

            <PlayButton
              className='ml-2'
              paused={activation.state.value === ActivationStateEnum.PAUSED.value}
              iconOnly
              pause={pauseActivation}
              play={resumeActivation}
              rbacPermission={RbacPermissions.ACTIVATION_EDIT}
              dataId={dataIdGenerator('')}
            />
          </div>

          <div className='center-flex-row mt-3'>
            {!activation.independentRunStatus.runsOnlyInWorkflows ? (
              <HdTooltip title={policyMessage}>
                <div className={clsx('execution-policy text-ellipsis', styles.executionPolicy)}>
                  <span className='text-secondary'>Runs</span> {policyMessage}
                </div>
              </HdTooltip>
            ) : (
              <WorkflowDependentExecutionPolicy
                independentRunStatus={activation.independentRunStatus}
              />
            )}

            <HdRbacLink
              tag='button'
              className='ml-1'
              icon='edit'
              rbacPermission={RbacPermissions.ACTIVATION_EDIT}
              dataId={dataIdGenerator('change-schedule')}
              onClick={changeFrequencyClick}
            >
              Change
            </HdRbacLink>
          </div>
        </div>

        <div className='secondary-nav__actions'>
          <HdIconButton
            id={MORE_ACTIONS_DROPDOWN_TRIGGER}
            verticalEdge='both'
            horizontalEdge='both'
            dataId={dataIdGenerator('more')}
            onClick={() => setMenuOpen(o => !o)}
          >
            <HdIcon name='more-vertical' size={3} />
          </HdIconButton>

          <HdMenu
            target={`#${MORE_ACTIONS_DROPDOWN_TRIGGER}`}
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
          >
            <HdRbacMenuItem onClick={runNow} rbacPermission={RbacPermissions.ACTIVATION_EDIT}>
              <HdIcon name='run-now' className='mr-3' />
              Run Now
            </HdRbacMenuItem>

            <HdRbacMenuItem
              onClick={restartActivation}
              rbacPermission={RbacPermissions.ACTIVATION_EDIT}
            >
              <HdIcon name='restart' className='mr-3' />
              Restart
            </HdRbacMenuItem>

            <HdRbacMenuItem
              onClick={deleteActivationClick}
              className='error has-separator'
              rbacPermission={RbacPermissions.ACTIVATION_DELETE}
            >
              <HdIcon name='delete' className='mr-3' />
              Delete
            </HdRbacMenuItem>
          </HdMenu>
        </div>
      </div>

      <ConfirmDialogV2
        title={ActivationStrings.deleteActivationTitle}
        body={ActivationStrings.deleteActivationBody}
        positiveButtonText={ActivationStrings.deleteActivationPositiveBtn}
        negativeButtonText={ActivationStrings.deleteActivationNegativeBtn}
        {...ENTITY_DELETE_DIALOG_COMMON_CONFIG_REACT}
        open={deleteConfirmationVisible}
        onClose={confirm => {
          setDeleteConfirmationVisible(false);

          if (confirm) {
            deleteActivation();
          }
        }}
      />

      <ExecutionPolicyDialog
        open={showExecutionPolicyDialog}
        entityDetails={activation}
        onClose={onExecutionPolicyDialogClose}
        entity={HevoEntity.ACTIVATION.value}
      />
    </header>
  );
}
